import React from 'react'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { type GridColDef, type GridSortModel } from '@mui/x-data-grid-premium'
import { FileCopyLineIcon, euiIconTypeMap } from '../../../components/icons'
import { LineItem } from '../../../types/demandRequest'
import { DemandRequestLineItemStatus } from '../../../graphql'
import { formatDate, formatDateTime, formatShopifyDate } from '../../../utils/formatters/date'
import { getLineItemTarget } from '../utils/formatters'
import LineItemActionsMenu from '../components/LineItemActionsMenu'
import LineItemStatusBadge from '../components/LineItemStatusBadge'
import { SMALL_GAP } from '../RequestDetails.constants'
import {
	inventoryItemMasterStatus,
	inventoryItemMasterDiagnostic,
} from '../services/inventoryItemMaster'

const CheckIcon = euiIconTypeMap.check
const AlertIcon = euiIconTypeMap.alert
const CrossIcon = euiIconTypeMap.cross
const MinusIcon = euiIconTypeMap.minus

type GetColumnsParams = {
	onCompleteLineItem: ( lineItemId: string ) => void,
	onReopenLineItem: ( lineItemId: string ) => void,
	onCancelLineItem: ( lineItemId: string ) => void,
	onEditFulfillerNote: ( lineItemId: string ) => void,
	onCopy: ( text: string, label: string ) => void,
	// The fulfilling location, so the IM column can evaluate Inventory Item
	// Master readiness (warehouse + bin) the same way the bulk action gates it.
	// Nullable since sc-571048: a stock request may have no fulfilling inventory
	// until one is chosen.
	fulfillingInventory?: { name: string, isWarehouse: boolean } | null,
	// Qty Avail. and IM describe one location. A request routed across
	// distribution centers has no single location to describe, and anything it
	// could have been filled from already has a fulfillment order, so they're
	// omitted there.
	showFulfillingInventoryStock: boolean,
	// Gates the demand-sourcing columns (available quantity + fulfillment status)
	// behind the Costentory:EnableDemandSourcing LaunchDarkly flag.
	enableDemandSourcing: boolean,
	// Gates the per-row "..." actions menu column behind the
	// CanEditDemandRequestByStoreId permission.
	canUserEditRequests: boolean,
}

/**
 * Sort comparator for Preferred Vendor column.
 * Case-insensitive; nulls (missing vendor or missing name) sort to the end in
 * both ascending and descending order — a row with no preferred vendor is
 * least actionable.
 */
export const comparePreferredVendor = ( a: LineItem, b: LineItem, direction: 'asc' | 'desc' ): number => {
	const na = a.product.preferredVendor?.name ?? null
	const nb = b.product.preferredVendor?.name ?? null
	if ( na === null && nb === null ) {
		return 0
	}
	if ( na === null ) {
		return 1
	}
	if ( nb === null ) {
		return -1
	}
	const cmp = na.localeCompare( nb, undefined, { sensitivity: 'base' } )
	return direction === 'asc' ? cmp : -cmp
}

export const PREFERRED_VENDOR_SORT_FIELD = 'preferredVendor'

/**
 * Column definitions for the Requested Items table.
 *
 * MUI's `field` is a row key, not a deep path, so every column reading a nested
 * value declares a flat `field` plus a `valueGetter`. Columns that render a
 * component off the whole row use `renderCell` with `params.row`.
 */
export const getRequestedItemsColumns = ( { onCompleteLineItem, onReopenLineItem, onCancelLineItem, onEditFulfillerNote, onCopy, fulfillingInventory, showFulfillingInventoryStock, enableDemandSourcing, canUserEditRequests }: GetColumnsParams ): GridColDef<LineItem>[] => {
	const qtyAvailableColumn: GridColDef<LineItem> = {
		field: 'availableQuantity',
		headerName: 'Qty Avail.',
		flex: 0.6,
		minWidth: 64,
		sortable: false,
		// Strict null check: 0 must render as "0", only null/undefined renders as "—"
		renderCell: ( { row } ) => {
			const quantity = row.fulfillingInventoryStorePart?.availableQuantity
			return quantity === null || quantity === undefined ? '—' : String( quantity )
		},
	}

	const itemMasterColumn: GridColDef<LineItem> = {
		field: 'hasActiveItemMaster',
		headerName: 'IM',
		// Icon-only, and the header is two characters.
		width: 48,
		valueGetter: ( _value, row ) => row.product.hasActiveItemMaster,
		// Reflects the full Inventory Item Master gate (active master + warehouse +
		// bin), so a green check means the item is fulfillable from this location —
		// matching what the bulk Create Fulfillment Order action allows. The alert
		// tooltip names the specific unmet condition.
		renderCell: ( { row } ) => {
			const status = inventoryItemMasterStatus( {
				hasActiveItemMaster: row.product.hasActiveItemMaster,
				locationIsWarehouse: fulfillingInventory?.isWarehouse ?? false,
				binLocation: row.fulfillingInventoryStorePart?.location,
			} )
			if ( status.isPresent ) {
				return (
					<Tooltip title="Has Inventory Item Master">
						<CheckIcon fontSize="small" color="success" />
					</Tooltip>
				)
			}
			const reason = inventoryItemMasterDiagnostic( status, {
				productLabel: row.product.partNumber ?? row.product.manufacturerNumber ?? row.product.name,
				locationName: fulfillingInventory?.name ?? 'The fulfilling location',
			} )
			return (
				<Tooltip title={reason ?? 'Missing Inventory Item Master'}>
					<AlertIcon fontSize="small" color="warning" />
				</Tooltip>
			)
		},
	}

	const fulfillmentColumn: GridColDef<LineItem> = {
		field: 'fulfillment',
		headerName: 'Fulfillment',
		flex: 0.9,
		// Floor is the "Cancelled"/"In Progress" badge width, not the header.
		minWidth: 100,
		sortable: false,
		renderCell: ( { row } ) => <LineItemStatusBadge lineItem={row} />,
	}

	// The per-row "..." actions menu is only shown to users who can edit demand
	// requests (CanEditDemandRequestByStoreId). Without the permission, the entire
	// column is omitted.
	const actionsColumn: GridColDef<LineItem> = {
		field: 'actions',
		headerName: '',
		// Icon-only: a small IconButton plus the compact cell's padding.
		width: 56,
		sortable: false,
		renderCell: ( { row } ) => (
			<LineItemActionsMenu
				lineItemId={row.lineItemId}
				status={row.status}
				fulfillerNotes={row.fulfillerNotes}
				onCompleteLineItem={onCompleteLineItem}
				onReopenLineItem={onReopenLineItem}
				onCancelLineItem={onCancelLineItem}
				onEditFulfillerNote={onEditFulfillerNote}
			/>
		),
	}

	return [
		{
			field: 'quantityRequested',
			headerName: 'Qty',
			flex: 0.5,
			minWidth: 52,
		},
		{
			field: 'partNumber',
			headerName: 'MFR #',
			// whiteSpace: nowrap on the value keeps the PN on a single line, so a
			// squeezed column ellipsizes rather than wrapping — which is why this can
			// flex down to a floor sized for a typical part number instead of holding
			// a fixed 150px that the narrow columns to its right then can't afford.
			flex: 0.9,
			minWidth: 120,
			valueGetter: ( _value, row ) => row.product.partNumber,
			renderCell: ( { value } ) => {
				if ( !value ) {
					return <Typography variant="inherit" component="strong">—</Typography>
				}
				return (
					<Stack direction="row" alignItems="center" gap={SMALL_GAP}>
						<Typography variant="inherit" component="strong" sx={{ whiteSpace: 'nowrap' }}>{value}</Typography>
						<Tooltip title="Copy part number">
							<IconButton
								onClick={() => onCopy( value, 'Part number' )}
								aria-label="Copy part number"
								size="small"
							>
								<FileCopyLineIcon fontSize="inherit" />
							</IconButton>
						</Tooltip>
					</Stack>
				)
			},
		},
		...( showFulfillingInventoryStock ? [ itemMasterColumn ] : [] ),
		{
			field: 'manufacturer',
			headerName: 'Manufacturer',
			flex: 1,
			minWidth: 84,
			valueGetter: ( _value, row ) => row.product.provider.name,
			renderCell: ( { value } ) => value ?? '—',
		},
		{
			field: 'description',
			headerName: 'Description',
			// Takes the largest share of the leftover width so a long description
			// doesn't starve the columns to its right. Wrapping (rather than
			// truncating) keeps the full value selectable for copy/paste — users
			// sometimes copy part info out of this cell.
			flex: 1.8,
			minWidth: 120,
			valueGetter: ( _value, row ) => row.product.partClass.description,
			renderCell: ( { value } ) => ( value ? (
				<Tooltip title={value}>
					<Typography variant="inherit">{value}</Typography>
				</Tooltip>
			) : '—' ),
		},
		{
			field: PREFERRED_VENDOR_SORT_FIELD,
			headerName: 'Preferred Vendor',
			flex: 1.1,
			minWidth: 84,
			valueGetter: ( _value, row ) => row.product.preferredVendor?.name,
			// Sorting is handled in the parent component (RequestedItemsTable) so we
			// can apply nulls-last semantics in both directions, which MUI's
			// negate-for-descending comparator cannot express. Returning a constant
			// makes MUI's stable sort preserve the pre-sorted order we pass in.
			sortComparator: () => 0,
			renderCell: ( { value } ) => value ?? '—',
		},
		{
			field: 'vendorPrice',
			headerName: 'Vendor Price',
			flex: 0.7,
			minWidth: 72,
			sortable: false,
			renderCell: ( { row } ) => {
				const price = row.product.preferredVendorPrice
				if ( !price ) {
					return '—'
				}
				return new Intl.NumberFormat( 'en-US', {
					style: 'currency',
					currency: price.currencyCode,
					maximumFractionDigits: 4,
					minimumFractionDigits: 2,
				} ).format( price.amount )
			},
		},
		...( enableDemandSourcing && showFulfillingInventoryStock ? [ qtyAvailableColumn ] : [] ),
		{
			field: 'itemIsFor',
			headerName: 'Item Is For',
			flex: 0.9,
			minWidth: 80,
			sortable: false,
			renderCell: ( { row } ) => getLineItemTarget( row ),
		},
		{
			field: 'neededBy',
			headerName: 'Needed By',
			flex: 0.8,
			// Floor holds the nowrap date alongside its copy button.
			minWidth: 88,
			renderCell: ( { value } ) => {
				if ( !value ) {
					return '—'
				}
				const shopifyDate = formatShopifyDate( value )
				return (
					<Stack direction="row" alignItems="center" gap={SMALL_GAP}>
						<Typography variant="inherit" sx={{ whiteSpace: 'nowrap' }}>{formatDate( value )}</Typography>
						{shopifyDate && (
							<Tooltip title="Copy date">
								<IconButton
									onClick={() => onCopy( shopifyDate, 'Needs by date' )}
									aria-label="Copy needs by date for Shopify"
									size="small"
								>
									<FileCopyLineIcon fontSize="inherit" />
								</IconButton>
							</Tooltip>
						)}
					</Stack>
				)
			},
		},
		{
			field: 'notes',
			headerName: 'Notes',
			// Notes get a generous share but no truncation — users frequently paste
			// reference info (PO numbers, work order IDs, URLs) and need to be able
			// to select and copy directly from the cell. The cell wraps internally
			// instead, growing the row's height rather than its width.
			flex: 1.4,
			minWidth: 100,
			sortable: false,
			renderCell: ( { row } ) => {
				const { lineNotes, fulfillerNotes } = row
				const hasNotes = lineNotes ?? fulfillerNotes

				if ( !hasNotes ) {
					return '—'
				}

				// Combine both notes with fulfiller notes prefixed
				const displayText = [
					lineNotes,
					fulfillerNotes ? `Fulfillment note: ${fulfillerNotes}` : null,
				]
					.filter( Boolean )
					.join( '\n' )

				return (
					<Tooltip title={<Typography variant="inherit" sx={{ whiteSpace: 'pre-wrap' }}>{displayText}</Typography>}>
						<Typography variant="inherit" sx={{ whiteSpace: 'pre-wrap' }}>{displayText}</Typography>
					</Tooltip>
				)
			},
		},
		{
			field: 'allowSubstitution',
			headerName: 'Allow Sub',
			flex: 0.6,
			// Yes/No — the floor is the header, which ellipsizes when squeezed.
			minWidth: 60,
			renderCell: ( { value } ) => ( value ? 'Yes' : 'No' ),
		},
		{
			field: 'willCall',
			headerName: 'Will Call',
			flex: 0.6,
			minWidth: 60,
			renderCell: ( { value } ) => ( value ? 'Yes' : 'No' ),
		},
		...( enableDemandSourcing ? [ fulfillmentColumn ] : [] ),
		{
			field: 'status',
			headerName: '',
			// Icon-only, no header.
			width: 44,
			renderCell: ( { row } ) => {
				if ( row.status === DemandRequestLineItemStatus.Cancelled ) {
					// Build tooltip content with date and user if available
					let tooltipContent = 'Cancelled'
					if ( row.dateCancelled ) {
						tooltipContent = `Cancelled on ${formatDateTime( row.dateCancelled )}`
						if ( row.cancelledByUser ) {
							tooltipContent += ` by ${row.cancelledByUser.firstName} ${row.cancelledByUser.lastName}`
						}
					}

					return (
						<Tooltip title={tooltipContent}>
							<CrossIcon fontSize="small" color="error" />
						</Tooltip>
					)
				}

				if ( row.status === DemandRequestLineItemStatus.Completed ) {
					return (
						<Tooltip title="Completed">
							<CheckIcon fontSize="small" color="success" />
						</Tooltip>
					)
				}

				return (
					<Tooltip title="Pending">
						<MinusIcon fontSize="small" color="disabled" />
					</Tooltip>
				)
			},
		},
		...( canUserEditRequests ? [ actionsColumn ] : [] ),
	]
}

/**
 * Default sorting configuration for the Requested Items table
 */
export const DEFAULT_SORT: GridSortModel = [
	{ field: 'manufacturer', sort: 'asc' },
]
